export type TeacherFeature = {
  slug: string;
  title: string;
  shortDescription: string;
  whatItDoes: string[];
  howItWorks: string[];
  benefits: string[];
  bestFor: string;
};

export const teacherFeatures: TeacherFeature[] = [
  {
    slug: "gestion-annonces",
    title: "Gestion complète de vos annonces",
    shortDescription:
      "Créez, modifiez et supprimez vos annonces avec paramétrage du prix, des modalités et des créneaux.",
    whatItDoes: [
      "Centralise la création et la mise à jour de vos annonces.",
      "Permet d'ajuster tarif, format (en ligne/présentiel) et contenus pédagogiques.",
      "Gère vos disponibilités pour afficher des créneaux cohérents.",
    ],
    howItWorks: [
      "Vous publiez votre annonce depuis votre dashboard tuteur.",
      "Chaque modification est immédiatement reflétée côté élèves.",
      "Les demandes reçues sont rattachées à l'annonce concernée.",
    ],
    benefits: [
      "Gain de temps dans la gestion quotidienne.",
      "Présentation claire de votre offre pour améliorer la conversion.",
      "Pilotage simple de votre activité même en période chargée.",
    ],
    bestFor:
      "Les tuteurs qui veulent structurer une offre claire et évolutive.",
  },
  {
    slug: "visibilite-seo",
    title: "Visibilité SEO et acquisition élèves",
    shortDescription:
      "Profitez de pages dédiées par ville, matière et profil tuteur pour augmenter votre visibilité auprès des familles.",
    whatItDoes: [
      "Expose vos annonces sur des pages optimisées pour la recherche.",
      "Multiplie vos points d'entrée grâce aux routes ville + matière.",
      "Améliore votre présence auprès des élèves qui recherchent activement un cours.",
    ],
    howItWorks: [
      "Votre profil est intégré dans des pages ciblées selon vos spécialités.",
      "Les contenus sont organisés pour un référencement local et thématique.",
      "Les élèves peuvent vous découvrir via des recherches naturelles.",
    ],
    benefits: [
      "Plus de visibilité sans effort marketing quotidien.",
      "Acquisition plus régulière d'élèves qualifiés.",
      "Positionnement durable sur votre zone d'enseignement.",
    ],
    bestFor:
      "Les tuteurs qui veulent développer leur activité sur une ville ou une matière.",
  },
  {
    slug: "demandes-structurees",
    title: "Demandes de cours structurées",
    shortDescription:
      "Recevez des demandes depuis vos annonces, puis acceptez ou refusez facilement avec un suivi clair des statuts.",
    whatItDoes: [
      "Organise les demandes entrantes dans un workflow simple.",
      "Permet d'accepter, refuser ou requalifier les demandes.",
      "Affiche l'avancement de chaque opportunité.",
    ],
    howItWorks: [
      "L'élève envoie une demande depuis votre annonce.",
      "Vous traitez la demande avec une décision explicite.",
      "Le suivi est historisé dans votre espace pour éviter les pertes d'information.",
    ],
    benefits: [
      "Moins d'allers-retours et de confusion.",
      "Décisions plus rapides sur les nouveaux élèves.",
      "Suivi commercial plus propre de votre pipeline.",
    ],
    bestFor: "Les tuteurs qui reçoivent plusieurs demandes par semaine.",
  },
  {
    slug: "invitations-eleves",
    title: "Invitations directes d'élèves",
    shortDescription:
      "Invitez un élève par email avec un lien de parcours dédié pour accélérer la mise en relation.",
    whatItDoes: [
      "Permet de créer une relation tuteur-élève en direct.",
      "Simplifie l'entrée d'un nouvel élève dans la plateforme.",
      "Réduit les frictions d'inscription quand vous avez déjà un contact.",
    ],
    howItWorks: [
      "Vous envoyez une invitation depuis votre dashboard.",
      "L'élève rejoint via un lien personnalisé.",
      "La relation est automatiquement reliée à votre espace de travail.",
    ],
    benefits: [
      "Onboarding plus rapide d'élèves recommandés.",
      "Moins de saisie manuelle et moins d'erreurs.",
      "Démarrage accéléré des premières séances.",
    ],
    bestFor:
      "Les tuteurs qui ont déjà des prospects en dehors de la plateforme.",
  },
  {
    slug: "cours-recurrents-ponctuels",
    title: "Cours récurrents ou ponctuels",
    shortDescription:
      "Organisez des bookings unitaires ou hebdomadaires/mensuels, avec gestion des confirmations, reports et annulations.",
    whatItDoes: [
      "Prend en charge les cours uniques et les cours récurrents.",
      "Structure l'agenda entre récurrence et besoins ponctuels.",
      "Maintient une continuité pédagogique sur le long terme.",
    ],
    howItWorks: [
      "Vous définissez les préférences de fréquence avec l'élève.",
      "Les séances sont planifiées et confirmées selon votre disponibilité.",
      "Les reports/annulations sont gérés dans le cycle de vie de session.",
    ],
    benefits: [
      "Agenda plus stable et revenus plus prévisibles.",
      "Meilleure fidélisation des élèves.",
      "Souplesse pour gérer examens, vacances et imprévus.",
    ],
    bestFor:
      "Les tuteurs qui combinent suivi régulier et accompagnements ponctuels.",
  },
  {
    slug: "rappels-suivi-sessions",
    title: "Rappels et suivi de sessions",
    shortDescription:
      "Automatisez les rappels de cours et pilotez le cycle de vie de chaque session jusqu'à sa réalisation.",
    whatItDoes: [
      "Automatise les rappels pour réduire les absences.",
      "Suit chaque session de la planification à l'exécution.",
      "Conserve un historique de vos cours réalisés et à venir.",
    ],
    howItWorks: [
      "Des rappels sont envoyés avant les séances.",
      "Les sessions changent de statut au fil des actions (planifiée, reportée, etc.).",
      "Vous visualisez rapidement les points d'attention de la semaine.",
    ],
    benefits: [
      "Moins de no-show et plus de régularité.",
      "Meilleure organisation au quotidien.",
      "Vision claire de votre charge de cours.",
    ],
    bestFor:
      "Les tuteurs qui veulent fiabiliser leur planning sans micro-gestion.",
  },
  {
    slug: "messagerie-integree",
    title: "Messagerie intégrée",
    shortDescription:
      "Échangez avec vos élèves via des conversations contextuelles, avec envoi de messages texte et pièces jointes.",
    whatItDoes: [
      "Réunit tous les échanges dans un espace unique.",
      "Relie chaque conversation à son contexte (annonce, demande, session).",
      "Autorise l'envoi de documents utiles aux cours.",
    ],
    howItWorks: [
      "Vous ouvrez une conversation liée à un élève/demande.",
      "Vous partagez messages, consignes et pièces jointes.",
      "Les réponses restent centralisées dans votre tableau de bord.",
    ],
    benefits: [
      "Communication plus fluide avec les familles.",
      "Historique complet pour mieux suivre la progression.",
      "Moins de dispersion entre plusieurs outils.",
    ],
    bestFor: "Les tuteurs qui veulent professionnaliser leur communication.",
  },
  {
    slug: "notifications",
    title: "Notifications in-app",
    shortDescription:
      "Suivez les nouvelles demandes, messages et actions importantes avec un centre de notifications consultable et nettoyable.",
    whatItDoes: [
      "Signale les événements importants en temps réel.",
      "Permet de marquer comme lu et nettoyer vos alertes.",
      "Accélère les actions prioritaires dans votre journée.",
    ],
    howItWorks: [
      "Chaque événement clé génère une notification.",
      "Vous accédez directement au contexte associé.",
      "Le flux peut être filtré ou vidé selon vos besoins.",
    ],
    benefits: [
      "Réactivité accrue sur les demandes et messages.",
      "Moins d'oublis sur les tâches critiques.",
      "Pilotage plus serein de votre activité.",
    ],
    bestFor: "Les tuteurs qui veulent garder le contrôle en un coup d'oeil.",
  },
  {
    slug: "paiements-facturation",
    title: "Paiements et facturation centralisés",
    shortDescription:
      "Bénéficiez d'une gestion Stripe complète: encaissements, synchronisation des statuts et suivi des factures.",
    whatItDoes: [
      "Centralise les paiements et les statuts financiers.",
      "Sécurise les flux de facturation via Stripe.",
      "Facilite la lecture de vos opérations.",
    ],
    howItWorks: [
      "Les paiements élèves sont traités dans un workflow dédié.",
      "Les statuts sont synchronisés automatiquement dans la plateforme.",
      "Vous consultez les informations utiles depuis votre dashboard.",
    ],
    benefits: [
      "Moins d'administratif manuel.",
      "Suivi financier plus fiable.",
      "Expérience de paiement rassurante pour les familles.",
    ],
    bestFor: "Les tuteurs qui veulent un cadre de monétisation robuste.",
  },
  {
    slug: "abonnements",
    title: "Abonnements adaptés à votre activité",
    shortDescription:
      "Choisissez un plan (ProfSimple 1.99, ProfPlus 9.99, ProfPro 19.99), puis ajustez votre formule selon votre croissance.",
    whatItDoes: [
      "Propose plusieurs plans selon votre niveau d'activité.",
      "Facilite les changements de formule au fil du temps.",
      "Maintient de la flexibilité si vos besoins évoluent.",
    ],
    howItWorks: [
      "Vous sélectionnez un plan depuis votre espace.",
      "Vous pouvez upgrader, downgrader ou annuler.",
      "La facturation suit votre choix de formule.",
    ],
    benefits: [
      "Coût aligné avec votre volume réel.",
      "Passage à l'échelle sans migration complexe.",
      "Vision claire du niveau de service utilisé.",
    ],
    bestFor:
      "Les tuteurs qui veulent une offre qui s'adapte à leur croissance.",
  },
  {
    slug: "google-calendar",
    title: "Intégration Google Calendar",
    shortDescription:
      "Connectez votre agenda Google, sélectionnez ou créez un calendrier et synchronisez automatiquement vos sessions.",
    whatItDoes: [
      "Synchronise vos séances avec votre agenda personnel.",
      "Réduit les doubles réservations.",
      "Maintient une vue centralisée de votre planning.",
    ],
    howItWorks: [
      "Vous connectez votre compte Google via OAuth.",
      "Vous choisissez le calendrier cible.",
      "Les sessions sont synchronisées automatiquement.",
    ],
    benefits: [
      "Moins d'oublis et de conflits d'horaire.",
      "Organisation simplifiée au quotidien.",
      "Vision claire de vos disponibilités réelles.",
    ],
    bestFor: "Les tuteurs qui pilotent plusieurs activités en parallèle.",
  },
  {
    slug: "zoom",
    title: "Visioconférence Zoom",
    shortDescription:
      "Configurez Zoom pour animer vos cours en ligne avec un parcours de connexion et de déconnexion intégré.",
    whatItDoes: [
      "Facilite l'animation des cours à distance.",
      "Intègre la gestion de votre connexion Zoom.",
      "Fluidifie le passage de la planification à la séance.",
    ],
    howItWorks: [
      "Vous activez votre connexion Zoom depuis votre espace.",
      "Les séances peuvent être associées à votre configuration visio.",
      "Vous gardez le contrôle de la connexion et de la déconnexion.",
    ],
    benefits: [
      "Expérience en ligne plus fluide pour l'élève.",
      "Moins de manipulations techniques avant chaque cours.",
      "Cadre professionnel pour le distanciel.",
    ],
    bestFor: "Les tuteurs qui donnent régulièrement des cours en ligne.",
  },
  {
    slug: "dashboard-tuteur",
    title: "Dashboard personnalisé tuteur",
    shortDescription:
      "Accédez à un espace complet pour piloter profil, disponibilités, élèves, sessions, paiements et activité.",
    whatItDoes: [
      "Réunit toutes vos données clés dans un tableau de bord unique.",
      "Offre des vues orientées action par domaine (élèves, sessions, paiements).",
      "Aide à piloter votre activité avec une logique opérationnelle.",
    ],
    howItWorks: [
      "Vous accédez à des onglets dédiés selon vos besoins.",
      "Chaque module est relié à vos données en temps réel.",
      "Vous prenez des décisions rapides sans changer d'outil.",
    ],
    benefits: [
      "Pilotage global plus efficace.",
      "Meilleure visibilité sur votre performance.",
      "Gain de temps sur l'administratif et la coordination.",
    ],
    bestFor:
      "Les tuteurs qui veulent gérer leur activité comme une vraie structure.",
  },
];

export const teacherFeatureGroups = [
  {
    title: "Acquisition et matching",
    featureSlugs: [
      "gestion-annonces",
      "visibilite-seo",
      "demandes-structurees",
      "invitations-eleves",
    ],
  },
  {
    title: "Organisation pédagogique",
    featureSlugs: [
      "cours-recurrents-ponctuels",
      "rappels-suivi-sessions",
      "messagerie-integree",
      "notifications",
    ],
  },
  {
    title: "Monétisation et outils",
    featureSlugs: [
      "paiements-facturation",
      "abonnements",
      "google-calendar",
      "zoom",
      "dashboard-tuteur",
    ],
  },
] as const;
